import { useState } from "react";
import { toast } from "sonner";
import { createItem, updateItem, deleteItem } from "@/lib/api";
import { Plus, Trash2, Check, X, Pencil } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const smallInput =
  "border-2 border-retro-ink bg-white px-3 py-2 font-medium text-retro-ink outline-none focus:border-retro-orange";

export const ItemManager = ({ items, onChange }) => {
  const [newName, setNewName] = useState("");
  const [newMax, setNewMax] = useState(1);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editMax, setEditMax] = useState(1);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return toast.error("Item name required.");
    try {
      await createItem({ name: newName.trim(), max_claims: Number(newMax) || 1 });
      setNewName(""); setNewMax(1);
      toast.success("Item added.");
      await onChange();
    } catch { toast.error("Could not add item."); }
  };

  const startEdit = (it) => { setEditing(it.id); setEditName(it.name); setEditMax(it.max_claims); };

  const saveEdit = async (id) => {
    try {
      await updateItem(id, { name: editName.trim(), max_claims: Number(editMax) || 1 });
      setEditing(null);
      toast.success("Item updated.");
      await onChange();
    } catch { toast.error("Could not update item."); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteItem(id);
      toast.success("Item deleted.");
      await onChange();
    } catch { toast.error("Could not delete item."); }
  };

  return (
    <div className="border-2 border-retro-ink bg-white shadow-retro-lg" data-testid="item-manager-card">
      <div className="border-b-2 border-retro-ink bg-retro-pink p-5">
        <h2 className="font-chango text-2xl text-white drop-shadow-[1px_1px_0px_#1A2A3A]">Item List</h2>
      </div>

      <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3 border-b-2 border-retro-ink bg-retro-cream p-5" data-testid="add-item-form">
        <div className="flex-1 min-w-[160px]">
          <label className="mb-1 block text-sm font-chango text-retro-ink">New item</label>
          <input data-testid="new-item-name" className={`${smallInput} w-full`} placeholder="e.g. Watermelon" value={newName} onChange={(e) => setNewName(e.target.value)} />
        </div>
        <div className="w-24">
          <label className="mb-1 block text-sm font-chango text-retro-ink">Max</label>
          <input type="number" min="1" data-testid="new-item-max" className={`${smallInput} w-full`} value={newMax} onChange={(e) => setNewMax(e.target.value)} />
        </div>
        <button type="submit" data-testid="add-item-button" className="inline-flex items-center gap-1 rounded-full border-2 border-retro-ink bg-retro-orange px-4 py-2 font-chango text-white shadow-retro-sm transition-[transform,box-shadow] hover:translate-x-[2px] hover:translate-y-[2px] hover:bg-retro-orangeDark hover:shadow-none">
          <Plus className="h-5 w-5" /> Add
        </button>
      </form>

      <div className="divide-y divide-retro-ink/15">
        {items.map((it) => {
          const isEditing = editing === it.id;
          const claimed = it.claim_count > 0;
          return (
            <div key={it.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`item-row-${it.id}`}>
              {isEditing ? (
                <>
                  <input className={`${smallInput} flex-1 min-w-[140px]`} data-testid={`edit-item-name-${it.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} />
                  <input type="number" min="1" className={`${smallInput} w-20`} data-testid={`edit-item-max-${it.id}`} value={editMax} onChange={(e) => setEditMax(e.target.value)} />
                  <button onClick={() => saveEdit(it.id)} data-testid={`save-item-${it.id}`} className="rounded-md border-2 border-retro-ink bg-retro-blue p-2 text-white"><Check className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(null)} data-testid={`cancel-edit-${it.id}`} className="rounded-md border-2 border-retro-ink bg-white p-2 text-retro-ink"><X className="h-4 w-4" /></button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-[140px]">
                    <span className="font-semibold text-retro-ink">{it.name}</span>
                  </div>
                  <span className="border-2 border-retro-ink bg-retro-yellow px-2.5 py-1 text-sm font-chango text-retro-ink" data-testid={`item-claims-${it.id}`}>
                    {it.claim_count}/{it.max_claims} claimed
                  </span>
                  <button onClick={() => startEdit(it)} data-testid={`edit-item-${it.id}`} className="rounded-md border-2 border-retro-ink bg-white p-2 text-retro-blue transition-colors hover:bg-retro-blue hover:text-white"><Pencil className="h-4 w-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-item-${it.id}`} className="rounded-md border-2 border-retro-ink bg-white p-2 text-destructive transition-colors hover:bg-destructive hover:text-white"><Trash2 className="h-4 w-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-2 border-retro-ink shadow-retro-lg">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-chango">Delete "{it.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {claimed
                            ? `⚠️ This item has ${it.claim_count} active claim(s). Deleting it will remove it from those guests' RSVPs. Are you sure?`
                            : "This item will be removed from the list."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`cancel-delete-item-${it.id}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`confirm-delete-item-${it.id}`} onClick={() => handleDelete(it.id)} className="bg-destructive text-white hover:bg-destructive/90">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
